import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { createUser } from '../../../services/ApiService';
import { useSystemTables } from '../../../hooks/UseSystemTables';
import Spinner from '../../../components/Spinner/Spinner';
import { Client } from '../../../models/SystemTables';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import styles from './CreateUser.module.css';
import { ApiError } from '../../../models/ApiError';
import { defaultErrorHandler } from '../../../services/ErrorHandler';

interface CreatedUserData {
  message: string;
  user: {
    username: string;
    tempPassword: string;
    attributes: {
      email: string;
      email_verified: string;
      phone_number: string;
      'custom:deletionProtected': string;
      'custom:clientInnerId': string;
      'custom:app': string;
      'custom:role': string;
      sub: string;
    };
  };
}

const CreateUser: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    phone_number: '',
    email: '',
    clientInnerId: '',
    'custom:role': '',
    'custom:app': '',
  });

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdUserData, setCreatedUserData] = useState<CreatedUserData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch system tables data
  const { data: systemTablesData, isLoading: isLoadingSystemTables, isError: systemTablesError } = useSystemTables();

  const userRoles = systemTablesData?.data.systemParameters?.userRole || [];
  const appTypes = systemTablesData?.data.systemParameters?.appType || [];
  const clients = systemTablesData?.data.clients || [];

  // Filter clients based on role and app values
  const filteredClients = useMemo(() => {
    const role = formData['custom:role'];
    const app = formData['custom:app'];

    // If role is "manager" and app is "userOrders": show clients whose clientInnerId appears as payingClientInnerId in another client
    if (role === 'manager' && app === 'userOrders') {
      // Get all payingClientInnerId values from clients
      const payingClientInnerIds = new Set(
        clients
          .map((client: Client) => client.payingClientInnerId)
          .filter((id: string) => id && id.trim() !== '')
      );

      // Filter clients whose clientInnerId appears in the payingClientInnerIds set
      return clients.filter((client: Client) =>
        payingClientInnerIds.has(client.clientInnerId)
      );
    }

    // If role is "client" and app is "userOrders": show only clients that have a value in their payingClientInnerId field
    if (role === 'client' && app === 'userOrders') {
      return clients.filter(
        (client: Client) =>
          client.payingClientInnerId &&
          client.payingClientInnerId.trim() !== ''
      );
    }

    // Default: show all clients
    return clients;
  }, [clients, formData['custom:role'], formData['custom:app']]);

  // Prepare select options from filtered clients
  const clientOptions = useMemo(
    () =>
      filteredClients.map((client: Client) => {
        const displayName = client.clientName || client.clientInnerId;
        const label = `${displayName} (${client.clientInnerId})`;
        return {
          value: client.clientInnerId,
          label: label,
        };
      }),
    [filteredClients]
  );

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      console.log('User created successfully:', data);
      setCreatedUserData(data);
      setSuccessDialogOpen(true);
      setFormErrors({});
      setErrorMessage(null);
    },
    onError: (error: any) => {
      console.error('Error creating user:', error);
      
      // Handle error using ErrorHandler
      if (ApiError.isApiError(error)) {
        // Extract field-specific errors
        const fieldErrors = defaultErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          // Set field-specific errors
          setFormErrors(fieldErrors);
          setErrorMessage(null);
        } else {
          // Set general error message
          const message = defaultErrorHandler.getUserFriendlyMessage(error);
          setErrorMessage(message);
          setFormErrors({});
        }
      } else {
        // Fallback for non-ApiError
        setErrorMessage(t('CreateUser.Errors.Generic', 'An error occurred while creating the user'));
        setFormErrors({});
      }
    },
  });

  // Handler for TextField inputs
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler for the Select component
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    
    // If role or app is changing, check if current clientInnerId is still valid
    if (name === 'custom:role' || name === 'custom:app') {
      const newRole = name === 'custom:role' ? value : formData['custom:role'];
      const newApp = name === 'custom:app' ? value : formData['custom:app'];
      
      // Check if current clientInnerId will still be valid after this change
      let isValidClient = true;
      
      if (newRole === 'manager' && newApp === 'userOrders') {
        const payingClientInnerIds = new Set(
          clients
            .map((client: Client) => client.payingClientInnerId)
            .filter((id: string) => id && id.trim() !== '')
        );
        isValidClient = payingClientInnerIds.has(formData.clientInnerId);
      } else if (newRole === 'client' && newApp === 'userOrders') {
        const client = clients.find(
          (c: Client) => c.clientInnerId === formData.clientInnerId
        );
        isValidClient = !!(
          client?.payingClientInnerId &&
          client.payingClientInnerId.trim() !== ''
        );
      }
      
      // Clear clientInnerId if it's no longer valid
      if (!isValidClient && formData.clientInnerId) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          clientInnerId: '',
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    // Clear field error when user selects a value
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler for Autocomplete component
  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    newValue: { value: string; label: string } | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      clientInnerId: newValue?.value || '',
    }));
    // Clear field error when user selects a value
    if (formErrors.clientInnerId) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.clientInnerId;
        return newErrors;
      });
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});
    setErrorMessage(null);
    createUserMutation.mutate(formData);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    navigate('/user-history');
  };

  const handleErrorClose = () => {
    setErrorMessage(null);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('CreateUser.Title', 'Create User')}</h2>

      {(createUserMutation.isPending || isLoadingSystemTables) && <Spinner />}
      {systemTablesError && (
        <Typography color="error">
          {t('CreateUser.Errors.Clients', 'Error loading client options')}
        </Typography>
      )}

      {!isLoadingSystemTables && (
        <form onSubmit={handleFormSubmit}>
          <TextField
            fullWidth
            margin="normal"
            name="username"
            label={t('CreateUser.Labels.UserName', 'User Name')}
            value={formData.username}
            onChange={handleInputChange}
            error={!!formErrors.username}
            helperText={formErrors.username}
          />

          <TextField
            fullWidth
            margin="normal"
            name="phone_number"
            label={t('CreateUser.Labels.PhoneNumber', 'Phone Number')}
            value={formData.phone_number}
            onChange={handleInputChange}
            error={!!formErrors.phone_number}
            helperText={formErrors.phone_number}
          />

          <TextField
            fullWidth
            margin="normal"
            name="email"
            label={t('CreateUser.Labels.Email', 'Email')}
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
          />

          <FormControl fullWidth margin="normal" error={!!formErrors['custom:role'] || !!formErrors.role}>
            <InputLabel>{t('User.Role')}</InputLabel>
            <Select
              name="custom:role"
              value={formData['custom:role']}
              onChange={handleSelectChange}
              label={t('User.Role')}
            >
              {userRoles.map((role) => (
                <MenuItem key={role._id} value={role.parameterValue}>
                  {i18n.language === 'he' ? role.hebName : role.engName}
                </MenuItem>
              ))}
            </Select>
            {(formErrors['custom:role'] || formErrors.role) && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {formErrors['custom:role'] || formErrors.role}
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth margin="normal" error={!!formErrors['custom:app'] || !!formErrors.app}>
            <InputLabel>{t('User.App')}</InputLabel>
            <Select
              name="custom:app"
              value={formData['custom:app']}
              onChange={handleSelectChange}
              label={t('User.App')}
            >
              {appTypes.map((app) => (
                <MenuItem key={app._id} value={app.parameterValue}>
                  {i18n.language === 'he' ? app.hebName : app.engName}
                </MenuItem>
              ))}
            </Select>
            {(formErrors['custom:app'] || formErrors.app) && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {formErrors['custom:app'] || formErrors.app}
              </Typography>
            )}
          </FormControl>

          <Autocomplete
            fullWidth
            options={clientOptions}
            getOptionLabel={(option) => option.label}
            value={clientOptions.find((opt: { value: string; label: string }) => opt.value === formData.clientInnerId) || null}
            onChange={handleAutocompleteChange}
            filterOptions={(options, { inputValue }) => {
              // If no input, show top 10
              if (!inputValue) {
                return options.slice(0, 10);
              }
              // If input exists, filter by label (case-insensitive)
              const filtered = options.filter((option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              );
              return filtered;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('CreateUser.Labels.ClientInnerId', 'Client Inner ID')}
                margin="normal"
                error={!!formErrors.clientInnerId}
                helperText={formErrors.clientInnerId}
              />
            )}
          />

          <Box mt={2}>
            <Button 
              variant="contained" 
              color="primary" 
              type="submit" 
              fullWidth
              disabled={createUserMutation.isPending}
            >
              {createUserMutation.isPending ? t('General.Labels.Submitting') || 'Submitting...' : t('General.Labels.Confirm', 'Confirm')}
            </Button>
          </Box>
        </form>
      )}

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
        dir={i18n.language === 'he' ? 'rtl' : 'ltr'}
      >
        <DialogTitle>
          {t('CreateUser.Success.Title', 'User Created Successfully')}
        </DialogTitle>
        <DialogContent>
          {createdUserData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>{t('User.UserName')}:</strong> {createdUserData.user.username}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{t('CreateUser.Success.TempPassword', 'Temporary Password')}:</strong> {createdUserData.user.tempPassword}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{t('User.Email')}:</strong> {createdUserData.user.attributes.email}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{t('User.PhoneNumber')}:</strong> {createdUserData.user.attributes.phone_number}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{t('User.Role')}:</strong> {userRoles.find(role => role.parameterValue === createdUserData.user.attributes['custom:role'])?.[i18n.language === 'he' ? 'hebName' : 'engName'] || createdUserData.user.attributes['custom:role']}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{t('User.App')}:</strong> {appTypes.find(app => app.parameterValue === createdUserData.user.attributes['custom:app'])?.[i18n.language === 'he' ? 'hebName' : 'engName'] || createdUserData.user.attributes['custom:app']}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('CreateUser.Success.Message', 'Please save these credentials. The temporary password will be required for the first login.')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} variant="contained" color="primary">
            {t('General.Labels.Close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreateUser;